import { createRouter, createWebHistory, isNavigationFailure } from "vue-router";
import routes from "@pages/routes";
import store from "@plugins/store";
import appConfigs from "@configs/app";
import i18n from "@plugins/i18n";
import { ROUTE_ACCESS, ROUTE_STORE, OBJECT_TYPE } from "./router.const";
import { sync } from "./service/router.sync.ts";
import {
  getRouteMetaField,
  getNearestRouteMetaField,
  handleRouteNext,
  routeNext,
  isBackNavigation,
  getPreventContentSubpageClose,
  resetNavigationFlags,
} from "./router.state";
import { isLoggedIn, isValidUrlDomain, saveRequestBeforeLogin, saveRequest } from "./router.auth";
import { redirectOldToNewSearchUrls } from "./router.redirect";
import { STUDENT_GROUP_RESOURCE_TYPE } from "@modules/assignment/service/assignment.constant";
import { isDefined } from "@utils/var.check";
import PERMISSION from "@/configs/permission";
import { cookieManager } from "@utils/cookie.manager";
import SessionManager from "@modules/auth/service/session.manager";
import User2FAApi from "@apis/user.2fa.api";
import AuthService from "@/modules/auth/service/auth.service";
import ContentSubpage from "@plugins/content-subpage";
import ContentSticky from "@plugins/content-sticky";
import Modal from "@plugins/modal";
import Auth from "@plugins/otp";
import { RESOURCE_DOMAIN_PATH } from "@plugins/common/constant/resource.constant";
import userActivityTrackEvent from "@plugins/user-activity-track/service/user.activity.track.event.ts";
import assignmentApi from "@apis/assignment.api";
import studentGroupResourceApi from "@apis/student.group.resource.api";
import { appLocalStorage } from "@utils/app.storage";
import { USER_PREFERENCE_LOCAL_STORAGE } from "@modules/user-preference/user-preference-constant";
import { USER_RECENTLY_ACCESSED_LOCAL_STORAGE } from "@modules/user-recently-accessed/user-recently-accessed-constant";
import { authStateChannel } from "@/modules/auth/service/auth.state.channel";
import {
  HTML_TOKEN_LAST_FETCH_STORAGE_KEY,
  HTML_TOKEN_STORAGE_KEY,
} from "@plugins/media/media.constant.ts";
import { RESOURCE_TOKEN_LAST_FETCH_STORAGE_KEY } from "@modules/auth/service/resource.signed.cookie.service.ts";

const user2FASessionManager = new SessionManager({
  namespace: "2FA",
  sessionExpiredInMilliSeconds: appConfigs.user2FASessionExpiredDuration,
  sessionWarningInMilliSeconds: appConfigs.user2FASessionWarningDuration,
  showExpiredPrompt: false,
  onExpire: () => {
    //empty
  },
  onExtend: () => {
    User2FAApi.check();
  },
});

async function getClassGroupId(objectType, objectId, action) {
  try {
    let response = null;
    if (objectType === OBJECT_TYPE.ASSIGNMENT || objectType === OBJECT_TYPE.ASSESSMENT) {
      response = store.getters["attempt/getStudentGroupCollectionUuid"];
      if (!response) {
        response = await assignmentApi.fetchAssignmentMetadata(objectId);
        return response.data.studentGroupCollectionUuid;
      }
      return response;
    } else if (objectType === OBJECT_TYPE.MODULE) {
      response =
        action === "attempt"
          ? store.getters["attempt/getStudentGroupCollectionUuid"]
          : store.getters["editor/lesson/getStudentGroupCollectionUuid"];

      if (!response) {
        response = await studentGroupResourceApi.fetchResourceAssignment({
          uuid: objectId,
          studentGroupResourceType: STUDENT_GROUP_RESOURCE_TYPE.CLASS_GROUP_RESOURCE,
        });
        return response.data.studentGroupCollectionUuid;
      }
      return response;
    }
  } catch (error) {
    console.error(error);
    return null;
  }
  return null;
}

async function trackResourceDomainEvent(to) {
  if (Object.values(RESOURCE_DOMAIN_PATH).some((path) => to.path.includes(path))) {
    const parsedData = await parseUrlPath(to.path);
    if (parsedData && Object.keys(parsedData).length > 0) {
      userActivityTrackEvent.resourceAction(parsedData, to);
    }
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return new Promise((resolve) => {
        const scrollTop = to?.query?.scrollTop ?? 0;
        setTimeout(() => {
          resolve({ left: 0, top: scrollTop });
        }, 200);
      });
    }
  },
});

router.beforeEach(function routeFilter(to, from, next) {
  //add role info for gtag
  let role = "none";
  if (store.getters["auth/isStudent"]) {
    role = "student";
  } else if (store.getters["auth/isTeacher"]) {
    role = "teacher";
  }
  const additionalInfo = {
    page_path: to.path,
    page_location: window.location.href,
    role: role,
  };

  if (window && window.gtag) {
    window.gtag("event", "page_view", additionalInfo);
  }

  isLoggedIn().then((loggedIn) => {
    // for tracking user activity pageView and custom event
    userActivityTrackEvent.pageView(to);
    trackResourceDomainEvent(to);

    // stay at current page if user already login
    if (to.path === "/login" && loggedIn && from.path !== "/") {
      return next(false);
    }

    // redirect to vle login or logout url
    if (to.path === "/logout") {
      if (loggedIn) {
        const loggedInFromSls = store.getters["auth/isLoggedInFromSls"];
        userActivityTrackEvent.logout();
        AuthService.logout().then(() => {
          history.replaceState(null, "Login", "/login");

          authStateChannel.logout(); // Notify other tabs

          redirect(loggedInFromSls ? appConfigs.loginUrl : appConfigs.edupassLogoutUrl);
        });
      } else {
        redirect(appConfigs.loginUrl);
      }
      return;
    }

    if (isBackNavigation(to) && ContentSubpage.$contentSubpage.hasOpenedContentSubpage()) {
      return next(false);
    }

    // public access
    if (ROUTE_ACCESS.PUBLIC === getRouteMetaField(to, "access")) {
      return handleRouteNext(to, from, next);
    }

    if (ROUTE_ACCESS.NOT_AUTHENTICATED === getRouteMetaField(to, "access")) {
      if (loggedIn) {
        if (to.path === "/login") {
          return routeNext(to, from, next, "/");
        }
        return next(false);
      }

      removeLocalStorageItems();
      return routeNext(to, from, next);
    }

    // non public access but not logged, so save requested url path and go to login page
    if (!loggedIn) {
      saveRequestBeforeLogin(from);
      removeLocalStorageItems();
      return routeNext(to, from, next, "/login");
    }

    // Force setup account
    const isRequiredToSetupAccount = store.getters["auth/hasPermission"](PERMISSION.ACCOUNT_SETUP);
    if (isRequiredToSetupAccount && to.path !== "/account/setup") {
      saveRequest();
      return routeNext(to, from, next, "/account/setup");
    }

    // Force change password
    const isForceChangePass = store.getters["auth/hasPermission"](PERMISSION.FORCE_CHANGE_PASS);
    if (isForceChangePass && to.path !== "/password/change") {
      saveRequest();
      return routeNext(to, from, next, "/password/change");
    }

    // Force acknowledge user advisory
    const isAcknowledgeUserAdvisory = store.getters["auth/hasPermission"](
      PERMISSION.REQUIRED_ACKNOWLEDGE_ADVISORY_NOTE,
    );
    if (isAcknowledgeUserAdvisory && to.path !== "/user/advisory") {
      saveRequest();
      return routeNext(to, from, next, "/user/advisory");
    }

    const setupFlow = isRequiredToSetupAccount || isForceChangePass || isAcknowledgeUserAdvisory;
    if (!setupFlow) {
      // redirect to saved request before login
      const savedRequestUrl = cookieManager.getCookie("SAVED_REQUEST_URL");
      cookieManager.removeCookie("SAVED_REQUEST_URL");
      if (isValidUrlDomain(savedRequestUrl) && to.path === "/") {
        return redirect(savedRequestUrl);
      }
    }

    if (to.path === "/404" && to.redirectedFrom) {
      userActivityTrackEvent.invalidUrl(to.redirectedFrom);
    }

    const requiredRole = getNearestRouteMetaField(to, "role");
    const requiredPermission = getNearestRouteMetaField(to, "permission");

    let permissionValid = true;
    let roleValid = true;
    if (isDefined(requiredPermission)) {
      permissionValid = store.getters["auth/hasPermission"](requiredPermission);
    }
    if (isDefined(requiredRole)) {
      roleValid = store.getters["auth/hasRole"](requiredRole);
    }

    const REQUIRE_2FA =
      ROUTE_ACCESS.TWO_FA_AUTHENTICATED === getRouteMetaField(to, "access") ||
      (ROUTE_ACCESS.PRIVILEGED_USER_TWO_FA_AUTHENTICATED === getRouteMetaField(to, "access") &&
        store.getters["auth/isPrivilegeUser"]);

    // 2FA restricted pages
    if (REQUIRE_2FA && roleValid) {
      // call api to check if session is 2FA authenticated
      return new Promise((resolve) => {
        User2FAApi.check().then((response) => {
          if (response.code === "ok" && response.data) {
            user2FASessionManager.resumeSessionDetect();
            return routeNext(to, from, next);
          }

          Auth.$auth.showOTPVerificationModal({
            title: i18n.t("user_2fa_modal_title"),
            content: i18n.t("email_verification_code_OTP_message"),
            actionPrimary: () => {
              resolve();
              user2FASessionManager.resumeSessionDetect();
              user2FASessionManager.init();
              return routeNext(to, from, next);
            },
            actionSecondary: () => {
              return routeNext(to, from, next, "/home");
            },
            requireSendOTP: true,
            sendOTP: User2FAApi.sendOTP,
            resendOTP: User2FAApi.resendOTP,
            verifyOTP: User2FAApi.authenticateOTP,
          });
        });
      });
    } else {
      user2FASessionManager.pauseSessionDetect();
    }

    if (redirectOldToNewSearchUrls(to, next)) {
      userActivityTrackEvent.invalidUrl(to);
      return;
    }

    // valid permission
    if (typeof requiredRole === "undefined" && permissionValid) {
      return handleRouteNext(to, from, next);
    }

    // valid role
    if (typeof requiredPermission === "undefined" && roleValid) {
      return handleRouteNext(to, from, next);
    }

    // valid role and permission
    if (roleValid && permissionValid) {
      return handleRouteNext(to, from, next);
    }

    // unauthorized access
    routeNext(to, from, next, "/403");
  });
});

router.afterEach((to, from, failure) => {
  //remove all instances of modals and content subpages on route change
  Modal.$modal.removeAll();

  if (!getPreventContentSubpageClose() && !isNavigationFailure(failure)) {
    ContentSubpage.$contentSubpage.removeAll();
  }
  ContentSticky.$stickpanel.remove();

  //SLSUAT-2029 - remove all wiris popup after navigating to other pages
  if (window.WirisPlugin && window.WirisPlugin.instances) {
    for (const key in window.WirisPlugin.instances) {
      const instance = window.WirisPlugin.instances[key];
      if (instance.core && instance.core.modalDialog && instance.core.modalDialog.close) {
        instance.core.modalDialog.close();
      }
    }
  }
  resetNavigationFlags();
});

sync(store, router, { moduleName: ROUTE_STORE });

export function removeLocalStorageItems() {
  appLocalStorage.removeItem(USER_PREFERENCE_LOCAL_STORAGE.LAST_UPDATED);
  appLocalStorage.removeItem(USER_PREFERENCE_LOCAL_STORAGE.SIGNED_URL_PARAMS);
  appLocalStorage.removeItem(USER_RECENTLY_ACCESSED_LOCAL_STORAGE.SIGNED_URL_PARAMS);
  appLocalStorage.removeItem(HTML_TOKEN_STORAGE_KEY);
  appLocalStorage.removeItem(HTML_TOKEN_LAST_FETCH_STORAGE_KEY);
  appLocalStorage.removeItem(RESOURCE_TOKEN_LAST_FETCH_STORAGE_KEY);
}

async function parseUrlPath(urlPath) {
  if (!urlPath || typeof urlPath !== "string") {
    return;
  }

  const segments = urlPath.split("/").filter(Boolean);

  let admin = false;

  if (segments[0] === "admin") {
    admin = true;
    segments.shift();
  }

  if (segments.length < 4) {
    return;
  }

  const [resourceDomain, objectType, action, objectId, ...remainingSegments] = segments;

  const parsedData = {
    admin: admin,
    resourceDomain,
    objectType,
    action,
    objectId,
    pathMap: { resourceDomain },
    context: {},
    actionScope: null,
  };

  // If resourceDomain is 'class-group', fetch classGroupId
  if (resourceDomain === RESOURCE_DOMAIN_PATH.CLASS_GROUP_RESOURCE) {
    const classGroupId = await getClassGroupId(objectType, objectId);
    parsedData.context.classGroupId = classGroupId;
  }

  // Process remaining segments as key-value pairs
  for (let i = 0; i < remainingSegments.length; i += 2) {
    if (i === remainingSegments.length - 1) {
      parsedData.actionScope = remainingSegments[i];
    } else {
      parsedData.pathMap[remainingSegments[i]] = remainingSegments[i + 1];
    }
  }

  return parsedData;
}

/**
 * @description This method check user auth before entering route
 */
export function redirect(url) {
  // Fix chrome bug https://stackoverflow.com/questions/8566222/javascript-window-redirect-doesnt-work-in-chrome-canceled-status
  // Add 100ms timeout to redirect page method
  setTimeout(function () {
    window.location.href = url;
  }, 100);
}

export { isValidRedirectUrlDomain } from "./router.redirect";
export { setPreventContentSubpageClose } from "./router.state";

export default router;
